import useDisableRubberBandScrolling from '@medflex/hooks/useDisableRubberBandScrolling';
import * as PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import useClassNames from '@medflex/hooks/useClassNames';
import ChatMessageStream from './ChatMessageStream/ChatMessageStream';
import ChatMessageStreamHeader from './ChatMessageStreamHeader';
import FloatingMessageBar from './FloatingMessageBar';
import DeletionNote from './DeletionNote/DeletionNote';
import Spinner from './Spinner';

const DEFAULT_ITEM_WINDOW_SIZE = 20;

const ChatStream = ({
  contact,
  userName,
  messages,
  errors,
  fileMessageClickHandler,
  fileMessageDownloadHandler,
  audioSourceResolver,
  PatientMessageAction,
  lastReadMessageId,
  newMessagesLabel,
  messageTypeLabels,
  MessageActionMenu,
  onSessionParticipate,
  deletedMessageText,
  messageSeenText,
  onScheduleDemo,
  anchor,
  header,
  classNames,
  deletionNoteText,
  deletionNoteUrl,
  isUserPatient,
  autoResponseMessage,
  isBasicUser,
  onWriteExamination,
}) => {
  const lastItemRef = useRef();
  const divRef = useDisableRubberBandScrolling();
  const firstElementRef = useRef();
  const [lastSeenItem, setLastSeenItem] = useState(null);

  const itemWindowSize = useMemo(() => {
    if (!anchor) return DEFAULT_ITEM_WINDOW_SIZE;

    const anchorMessageIndex = messages.findIndex(message => message.id === anchor);

    if (anchorMessageIndex < 0) return DEFAULT_ITEM_WINDOW_SIZE;

    return Math.max(messages.length - anchorMessageIndex, DEFAULT_ITEM_WINDOW_SIZE);
  }, [messages, anchor]);

  const [[start, limit], setItemsWindow] = useState([0, itemWindowSize]);

  useEffect(() => {
    if (limit > itemWindowSize && lastSeenItem) {
      window.requestAnimationFrame(() => {
        lastSeenItem.scrollIntoView({ block: 'center' });
      });
    }
  }, [lastSeenItem, limit, itemWindowSize]);

  useEffect(() => {
    if (messages.length > limit && firstElementRef.current) {
      let ticking = false;

      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            if (firstElementRef.current) {
              const { top } = firstElementRef.current.getBoundingClientRect();
              if (top > 0) {
                setLastSeenItem(
                  divRef?.current?.querySelector('[data-component=ChatMessageBubble]'),
                );
                setItemsWindow([start, limit + itemWindowSize]);
              }
            }
            ticking = false;
          });

          ticking = true;
        }
      };

      const refVal = divRef.current;
      refVal?.addEventListener('scroll', handleScroll);

      return () => {
        if (refVal) {
          refVal.removeEventListener('scroll', handleScroll);
        }
      };
    }
    return () => {};
  }, [start, limit, divRef, messages, firstElementRef, itemWindowSize]);

  const cn = useClassNames(
    () => [
      classNames,
      'absolute',
      'flex',
      'flex-col',
      'inset-0',
      'overflow-x-hidden',
      'overflow-y-auto',
      'pb-2',
    ],
    [classNames],
  );

  useEffect(() => {
    if (lastItemRef && lastItemRef.current && lastItemRef.current.scrollIntoView && !anchor) {
      lastItemRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [messages.length, anchor]);

  return (
    <div data-component="ChatStream" className="relative flex flex-1 overflow-hidden">
      {errors && <FloatingMessageBar errors={errors.connectionErrors} className="m-6" />}
      {errors && <FloatingMessageBar bottom errors={errors.messageErrors} className="m-6" />}
      <div className={cn} ref={divRef}>
        {header}
        <div ref={firstElementRef} />
        {limit < messages.length && (
          <div className="mb-2 w-full text-center">
            <Spinner size="lg" />
          </div>
        )}
        <ChatMessageStream
          messages={messages.slice(-limit)}
          contactName={contact && contact.fullNameWithTitle}
          userName={userName}
          lastReadMessageId={lastReadMessageId}
          newMessagesLabel={newMessagesLabel}
          messageTypeLabels={messageTypeLabels}
          fileMessageClickHandler={fileMessageClickHandler}
          fileMessageDownloadHandler={fileMessageDownloadHandler}
          audioSourceResolver={audioSourceResolver}
          PatientMessageAction={PatientMessageAction}
          MessageActionMenu={MessageActionMenu}
          onSessionParticipate={onSessionParticipate}
          deletedMessageText={deletedMessageText}
          onScheduleDemo={onScheduleDemo}
          anchor={anchor}
          messageSeenText={messageSeenText}
          isUserPatient={isUserPatient}
          autoResponseMessage={autoResponseMessage}
          isBasicUser={isBasicUser}
          onWriteExamination={onWriteExamination}
        />
        <div ref={lastItemRef} className="mb-8" />
        {deletionNoteText && (
          <div className="flex flex-1 items-end justify-center">
            <DeletionNote deletionNoteText={deletionNoteText} deletionNoteUrl={deletionNoteUrl} />
          </div>
        )}
      </div>
    </div>
  );
};

ChatStream.propTypes = {
  contact: ChatMessageStreamHeader.propTypes.contact,
  messages: ChatMessageStream.propTypes.messages,
  errors: PropTypes.shape({
    connectionErrors: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.oneOf(['alert', 'warning']),
        title: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
      }),
    ),
    messageErrors: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.oneOf(['alert', 'warning']),
        title: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
      }),
    ),
  }),
  fileMessageClickHandler: ChatMessageStream.propTypes.fileMessageClickHandler,
  fileMessageDownloadHandler: ChatMessageStream.propTypes.fileMessageDownloadHandler,
  audioSourceResolver: ChatMessageStream.propTypes.audioSourceResolver,
  PatientMessageAction: ChatMessageStream.propTypes.PatientMessageAction,
  newMessagesLabel: PropTypes.string,
  messageTypeLabels: ChatMessageStream.propTypes.messageTypeLabels,
  onSessionParticipate: ChatMessageStream.propTypes.onSessionParticipate,
  lastReadMessageId: PropTypes.string,
  MessageActionMenu: PropTypes.func,
  deletedMessageText: ChatMessageStream.propTypes.deletedMessageText,
  userName: PropTypes.string,
  anchor: PropTypes.string,
  deletionNoteText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  deletionNoteUrl: PropTypes.string,
  onScheduleDemo: ChatMessageStream.propTypes.onScheduleDemo,
  header: PropTypes.node,
  messageSeenText: PropTypes.string,
  classNames: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  isUserPatient: PropTypes.bool,
  autoResponseMessage: PropTypes.string,
  isBasicUser: PropTypes.bool,
  onWriteExamination: PropTypes.func,
};

ChatStream.defaultProps = {
  contact: {},
  messages: [],
  errors: null,
  fileMessageClickHandler: () => {},
  fileMessageDownloadHandler: () => {},
  audioSourceResolver: () => {},
  PatientMessageAction: null,
  newMessagesLabel: null,
  messageTypeLabels: null,
  onSessionParticipate: () => {},
  lastReadMessageId: null,
  MessageActionMenu: null,
  deletedMessageText: null,
  userName: null,
  anchor: null,
  deletionNoteText: null,
  deletionNoteUrl: null,
  onScheduleDemo: () => {},
  header: null,
  messageSeenText: null,
  classNames: null,
  autoResponseMessage: null,
  isUserPatient: false,
  isBasicUser: false,
  onWriteExamination: null,
};

export default ChatStream;
